import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const apiUrl = import.meta.env.VITE_API_URL;

const QuoteForm = () => {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [activeTab, setActiveTab] = useState("subContract");
  const [subContractFormData, setSubContractFormData] = useState({
    clientName: "",
    projectName: "",
    projectType: "",
    projectValue: "",
    phone: "",
    email: "",
    description: "",
    attachment: null,
  });

  const [supplyFormData, setSupplyFormData] = useState({
    clientName: "",
    projectName: "",
    supplyFor: "",
    contractPeriod: "",
    phone: "",
    email: "",
    trades: [{ trade: "", qty: "", pricePerHour: "", pricePerMonth: "" }],
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // More robust country checking
    const country = localStorage.getItem("selectedCountry") || "";
    setSelectedCountry(country);

    // Set default tab based on country
    if (
      country.toLowerCase().includes("saudi") ||
      country.toLowerCase().includes("arabia")
    ) {
      setActiveTab("subContract");
    } else {
      setActiveTab("subContract"); // Default to subContract for other countries
    }
  }, []);

  const handleSubContractChange = (e) => {
    const { name, value } = e.target;
    setSubContractFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSupplyChange = (e, index) => {
    const { name, value } = e.target;
    const updatedTrades = [...supplyFormData.trades];

    if (name.startsWith("trade-")) {
      updatedTrades[index] = {
        ...updatedTrades[index],
        [name.split("-")[1]]: value,
      };
      setSupplyFormData((prev) => ({
        ...prev,
        trades: updatedTrades,
      }));
    } else {
      setSupplyFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const addTradeField = () => {
    setSupplyFormData((prev) => ({
      ...prev,
      trades: [
        ...prev.trades,
        { trade: "", qty: "", pricePerHour: "", pricePerMonth: "" },
      ],
    }));
  };

  const removeTradeField = (index) => {
    if (supplyFormData.trades.length > 1) {
      const updatedTrades = [...supplyFormData.trades];
      updatedTrades.splice(index, 1);
      setSupplyFormData((prev) => ({
        ...prev,
        trades: updatedTrades,
      }));
    }
  };

  const handleFileChange = (e) => {
    setSubContractFormData((prev) => ({
      ...prev,
      attachment: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const endpoint = activeTab === "subContract" ? "subcontract" : "supply";
      const formData =
        activeTab === "subContract" ? subContractFormData : supplyFormData;

      const response = await fetch(`${apiUrl}/quote/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit quote request");
      }

      setSubmitSuccess(true);
      // Reset form data
      if (activeTab === "subContract") {
        setSubContractFormData({
          clientName: "",
          projectName: "",
          projectType: "",
          projectValue: "",
          phone: "",
          email: "",
          description: "",
          attachment: null,
        });
      } else {
        setSupplyFormData({
          clientName: "",
          projectName: "",
          supplyFor: "",
          contractPeriod: "",
          phone: "",
          email: "",
          trades: [{ trade: "", qty: "", pricePerHour: "", pricePerMonth: "" }],
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const projectTypes = [
    "Residential Building",
    "Commercial Building",
    "Industrial Facility",
    "Infrastructure Project",
    "Renovation",
    "Other",
  ];

  const supplyForOptions = [
    "Indoor Labour",
    "Cleaning Services",
    "Construction Materials",
    "Equipment Rental",
    "Security Services",
    "Other",
  ];

  const contractPeriods = [
    "3 months",
    "6 months",
    "1 year",
    "1 year + extension",
    "2 years",
    "Open-ended",
  ];

  const tradeOptions = [
    "Carpentry",
    "Electrical",
    "Plumbing",
    "Masonry",
    "Painting",
    "HVAC",
    "Steel Work",
    "Other",
  ];

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="py-16 px-4 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl pt-12 font-bold text-[#040404] mb-4">
            Request a <span className="text-[#7bbf42]">Quote</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get customized pricing for your project needs. Fill out the form
            below and our team will contact you shortly.
          </p>
        </motion.div>

        {submitSuccess ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#7bbf42]/10 border border-[#7bbf42] rounded-xl p-8 text-center"
          >
            {/* Success message UI remains the same */}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200"
          >
            <div className="h-2 bg-gradient-to-r from-[#7bbf42] via-[#f9b414] to-[#040404]"></div>

            {/* Improved toggle section with better visibility */}
            {(selectedCountry.toLowerCase().includes("saudi") ||
              selectedCountry.toLowerCase().includes("arabia")) && (
              <div className="flex border-b border-gray-200 bg-gray-50">
                <button
                  onClick={() => setActiveTab("subContract")}
                  className={`flex-1 py-4 font-medium text-lg transition-colors duration-300 ${
                    activeTab === "subContract"
                      ? "bg-[#7bbf42] text-white shadow-md"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Sub Contract
                  </motion.div>
                </button>
                <button
                  onClick={() => setActiveTab("supply")}
                  className={`flex-1 py-4 font-medium text-lg transition-colors duration-300 ${
                    activeTab === "supply"
                      ? "bg-[#7bbf42] text-white shadow-md"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Supply
                  </motion.div>
                </button>
              </div>
            )}

            <div className="p-8">
              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg"
                >
                  {error}
                </motion.div>
              )}

              <AnimatePresence mode="wait">
                {activeTab === "subContract" ? (
                  <motion.form
                    key="subContract"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={handleSubmit}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="clientName"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Client Name <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="text"
                          id="clientName"
                          name="clientName"
                          value={subContractFormData.clientName}
                          onChange={handleSubContractChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="projectName"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Name of Project{" "}
                          <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="text"
                          id="projectName"
                          name="projectName"
                          value={subContractFormData.projectName}
                          onChange={handleSubContractChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="projectType"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Project Type <span className="text-[#f9b414]">*</span>
                        </label>
                        <select
                          id="projectType"
                          name="projectType"
                          value={subContractFormData.projectType}
                          onChange={handleSubContractChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        >
                          <option value="">Select Project Type</option>
                          {projectTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="projectValue"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Value of Project (SAR){" "}
                          <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="number"
                          id="projectValue"
                          name="projectValue"
                          value={subContractFormData.projectValue}
                          onChange={handleSubContractChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Phone Number <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={subContractFormData.phone}
                          onChange={handleSubContractChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Email ID <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={subContractFormData.email}
                          onChange={handleSubContractChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                        className="md:col-span-2"
                      >
                        <label
                          htmlFor="description"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Project Description{" "}
                          <span className="text-[#f9b414]">*</span>
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          value={subContractFormData.description}
                          onChange={handleSubContractChange}
                          required
                          rows="4"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                          placeholder="Describe your project requirements in detail..."
                        ></textarea>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                        className="md:col-span-2"
                      >
                        <label
                          htmlFor="attachment"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Attachment (Optional)
                        </label>
                        <div className="flex items-center">
                          <label className="flex flex-col items-center px-4 py-6 bg-white rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50">
                            <svg
                              className="w-8 h-8 text-[#7bbf42]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              ></path>
                            </svg>
                            <span className="mt-2 text-sm text-gray-600">
                              Upload File
                            </span>
                            <input
                              id="attachment"
                              name="attachment"
                              type="file"
                              className="hidden"
                              onChange={handleFileChange}
                            />
                          </label>
                          {subContractFormData.attachment && (
                            <span className="ml-4 text-sm text-gray-700">
                              {subContractFormData.attachment.name}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    </div>

                    <motion.button
                      whileHover={{
                        scale: 1.03,
                        boxShadow: "0 5px 15px rgba(123, 191, 66, 0.3)",
                      }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 px-6 bg-gradient-to-r from-[#7bbf42] to-[#f9b414] text-white font-bold rounded-lg text-lg transition-all duration-300 flex items-center justify-center"
                    >
                      {submitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        "Request Sub Contract Quote"
                      )}
                    </motion.button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="supply"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={handleSubmit}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="supply-clientName"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Client Name <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="text"
                          id="supply-clientName"
                          name="clientName"
                          value={supplyFormData.clientName}
                          onChange={handleSupplyChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="supply-projectName"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Name of Project/Organization{" "}
                          <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="text"
                          id="supply-projectName"
                          name="projectName"
                          value={supplyFormData.projectName}
                          onChange={handleSupplyChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="supplyFor"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Supply For <span className="text-[#f9b414]">*</span>
                        </label>
                        <select
                          id="supplyFor"
                          name="supplyFor"
                          value={supplyFormData.supplyFor}
                          onChange={handleSupplyChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        >
                          <option value="">Select Supply Type</option>
                          {supplyForOptions.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="contractPeriod"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Contract Period{" "}
                          <span className="text-[#f9b414]">*</span>
                        </label>
                        <select
                          id="contractPeriod"
                          name="contractPeriod"
                          value={supplyFormData.contractPeriod}
                          onChange={handleSupplyChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        >
                          <option value="">Select Contract Period</option>
                          {contractPeriods.map((period) => (
                            <option key={period} value={period}>
                              {period}
                            </option>
                          ))}
                        </select>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="supply-phone"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Phone Number <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="tel"
                          id="supply-phone"
                          name="phone"
                          value={supplyFormData.phone}
                          onChange={handleSupplyChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <label
                          htmlFor="supply-email"
                          className="block text-sm font-medium text-[#040404] mb-1"
                        >
                          Email ID <span className="text-[#f9b414]">*</span>
                        </label>
                        <input
                          type="email"
                          id="supply-email"
                          name="email"
                          value={supplyFormData.email}
                          onChange={handleSupplyChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                        className="md:col-span-2"
                      >
                        <h3 className="text-lg font-medium text-[#040404] mb-4">
                          Trade Details
                        </h3>
                        <div className="space-y-6">
                          {supplyFormData.trades.map((trade, index) => (
                            <div
                              key={index}
                              className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg"
                            >
                              <div>
                                <label
                                  htmlFor={`trade-${index}`}
                                  className="block text-sm font-medium text-[#040404] mb-1"
                                >
                                  Trade{" "}
                                  <span className="text-[#f9b414]">*</span>
                                </label>
                                <select
                                  id={`trade-${index}`}
                                  name={`trade-trade`}
                                  value={trade.trade}
                                  onChange={(e) => handleSupplyChange(e, index)}
                                  required
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                                >
                                  <option value="">Select Trade</option>
                                  {tradeOptions.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label
                                  htmlFor={`qty-${index}`}
                                  className="block text-sm font-medium text-[#040404] mb-1"
                                >
                                  Quantity{" "}
                                  <span className="text-[#f9b414]">*</span>
                                </label>
                                <input
                                  type="number"
                                  id={`qty-${index}`}
                                  name={`trade-qty`}
                                  value={trade.qty}
                                  onChange={(e) => handleSupplyChange(e, index)}
                                  required
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                                />
                              </div>
                              <div>
                                <label
                                  htmlFor={`pricePerHour-${index}`}
                                  className="block text-sm font-medium text-[#040404] mb-1"
                                >
                                  Price/Hour (SAR)
                                </label>
                                <input
                                  type="number"
                                  id={`pricePerHour-${index}`}
                                  name={`trade-pricePerHour`}
                                  value={trade.pricePerHour}
                                  onChange={(e) => handleSupplyChange(e, index)}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                                />
                              </div>
                              <div>
                                <label
                                  htmlFor={`pricePerMonth-${index}`}
                                  className="block text-sm font-medium text-[#040404] mb-1"
                                >
                                  Price/Month (SAR)
                                </label>
                                <input
                                  type="number"
                                  id={`pricePerMonth-${index}`}
                                  name={`trade-pricePerMonth`}
                                  value={trade.pricePerMonth}
                                  onChange={(e) => handleSupplyChange(e, index)}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7bbf42] focus:border-[#7bbf42] transition-all"
                                />
                              </div>
                              <div className="md:col-span-4 flex justify-end space-x-2">
                                {supplyFormData.trades.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeTradeField(index)}
                                    className="px-3 py-1 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                  >
                                    Remove
                                  </button>
                                )}
                                {index === supplyFormData.trades.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={addTradeField}
                                    className="px-3 py-1 bg-[#7bbf42] text-white rounded-lg hover:bg-[#6aa837] transition-colors"
                                  >
                                    Add Trade
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    </div>

                    <motion.button
                      whileHover={{
                        scale: 1.03,
                        boxShadow: "0 5px 15px rgba(123, 191, 66, 0.3)",
                      }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 px-6 bg-gradient-to-r from-[#7bbf42] to-[#f9b414] text-white font-bold rounded-lg text-lg transition-all duration-300 flex items-center justify-center"
                    >
                      {submitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        "Request Supply Quote"
                      )}
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </div>
    </motion.section>
  );
};

export default QuoteForm;
